import React from 'react';
import { MapPin, Globe } from 'lucide-react';

/**
 * Компонент секции выбора места назначения командировки
 */
const DestinationSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  russianRegions,
  foreignCountries 
}) => {
  return (
    <div className="mb-6">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <MapPin className="w-5 h-5 mr-2 text-indigo-500" />
        Место назначения
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Тип командировки */}
        <div className="col-span-full">
          <div className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип командировки
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="destination"
                value="domestic"
                checked={formData.destination === 'domestic'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                <MapPin className="w-4 h-4 mr-1 text-blue-500" />
                По России
              </span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="destination"
                value="international"
                checked={formData.destination === 'international'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center">
                <Globe className="w-4 h-4 mr-1 text-green-500" />
                Зарубежная
              </span>
            </label>
          </div>
        </div>

        {/* Регион России */}
        {formData.destination === 'domestic' && (
          <div className="col-span-full">
            <label htmlFor="region" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Регион командировки
            </label>
            <select
              id="region"
              name="region"
              value={formData.region}
              onChange={handleInputChange}
              className={`form-select w-full ${errors.region ? 'input-error' : ''}`}
            >
              {russianRegions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.name} (суточные: {region.dailyRate} ₽)
                </option>
              ))}
            </select>
            {errors.region && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.region}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Выберите регион или город командировки. От этого зависит размер суточных.
            </p>
          </div>
        )}

        {/* Страна зарубежной командировки */}
        {formData.destination === 'international' && (
          <div className="col-span-full">
            <label htmlFor="country" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Страна командировки
            </label>
            <select
              id="country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              className={`form-select w-full ${errors.country ? 'input-error' : ''}`}
            >
              <option value="none">Выберите страну...</option>
              {foreignCountries.map((country) => (
                <option key={country.id} value={country.id}>
                  {country.name} (суточные: {country.dailyRate} ₽)
                </option>
              ))}
            </select>
            {errors.country && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.country}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Выберите страну командировки. От этого зависит размер суточных по нормам Минфина РФ.
            </p>
          </div>
        )}

        {/* Ставка суточных */}
        <div className="col-span-full">
          <div className="flex items-center mb-2">
            <label htmlFor="customDailyRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Ставка суточных
            </label>
            <label className="inline-flex items-center ml-4">
              <input
                type="checkbox"
                name="rateOverride"
                checked={formData.rateOverride}
                onChange={handleInputChange}
                className="form-checkbox"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Свои ставки
              </span>
            </label>
          </div>
          <div className="flex">
            <input
              type="text"
              id="customDailyRate"
              name="customDailyRate"
              value={formData.customDailyRate}
              onChange={handleInputChange}
              disabled={!formData.rateOverride}
              className={`form-input w-full ${errors.customDailyRate ? 'input-error' : ''}`}
              placeholder="Введите ставку суточных"
            />
            <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-sm rounded-r-md">
              ₽/день
            </span>
          </div>
          {errors.customDailyRate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.customDailyRate}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {formData.rateOverride 
              ? 'Указана ваша ставка суточных. Отключите "Свои ставки", чтобы использовать нормативы.'
              : 'Используется нормативная ставка суточных для выбранного региона/страны с учетом категории сотрудника.'}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DestinationSection;